import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { getInvoices, deleteInvoice, getAllUsersDropdown } from '../services/api';
import { formatDate } from '../utils/dateUtils';
import { Search, Eye, Trash2, Receipt, Loader2, Download, User } from 'lucide-react';

const STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'submission', label: 'For Submission' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid', label: 'Paid' },
  { value: 'exported', label: 'Exported' },
];

const statusBadge = (status) => {
  const map = {
    0: { label: 'For Submission', color: 'bg-slate-100 text-slate-600' },
    1: { label: 'Unpaid', color: 'bg-yellow-100 text-yellow-700' },
    2: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  };
  const s = map[status] || map[0];
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.color}`} data-testid={`invoice-status-${status}`}>{s.label}</span>;
};

const exportedBadge = () => (
  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 ml-1" data-testid="exported-badge">Exported</span>
);

function RemoveModal({ isOpen, invoice, onClose, onConfirm, loading }) {
  if (!isOpen || !invoice) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[10px] w-full max-w-sm">
        <div className="p-6 text-center">
          <h3 className="font-semibold mb-2">Remove Invoice confirmation</h3>
          <p className="text-sm text-slate-500">Are you sure you want to remove Invoice with invoice number <b>{invoice.invoice_number}</b>?</p>
          <p className="text-sm mt-1">Click confirm to continue</p>
        </div>
        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-sm text-sm" data-testid="cancel-remove-invoice">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="px-4 py-2 bg-red-500 text-white rounded-sm text-sm flex items-center gap-2" data-testid="confirm-remove-invoice">
            {loading && <Loader2 size={14} className="animate-spin" />}Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function Invoices() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [removeTarget, setRemoveTarget] = useState(null);
  const [createdByFilter, setCreatedByFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: staffUsers } = useQuery({
    queryKey: ['usersDropdownAll'],
    queryFn: () => getAllUsersDropdown().then(res => res.data),
  });

  // Build query params based on active tab
  const queryParams = (() => {
    const p = {};
    switch (activeTab) {
      case 'submission': p.status = 0; break;
      case 'unpaid': p.status = 1; break;
      case 'paid': p.status = 2; break;
      case 'exported': p.exported = true; break;
      default: break;
    }
    if (createdByFilter) p.user_filter = createdByFilter;
    return p;
  })();

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['invoices', activeTab, createdByFilter],
    queryFn: () => getInvoices(queryParams).then(res => res.data)
  });

  const deleteMutation = useMutation({
    mutationFn: deleteInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setRemoveTarget(null);
      toast.success('Invoice has been removed');
    }
  });

  const filtered = invoices?.filter(i =>
    (i.invoice_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.client_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.reference || '').toLowerCase().includes(search.toLowerCase())
  );

  // formatDate imported from utils/dateUtils

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Invoices</h1>
          <p className="text-slate-500 text-sm mt-1">Manage billing and payments</p>
        </div>
        <Link
          to="/invoices/export-xero"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-sm text-sm font-medium hover:bg-amber-600"
          data-testid="export-xero-btn"
        >
          <Download size={16} />
          Export Xero Invoice
        </Link>
      </div>

      {/* Status Tabs */}
      <div className="bg-white rounded-[10px] border p-3">
        <div className="flex flex-wrap gap-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                activeTab === tab.value
                  ? 'bg-primary text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`invoice-tab-${tab.value}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-[10px] border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by invoice number, client, or reference..."
              className="w-full pl-10 pr-4 py-2 border rounded-none text-sm"
              data-testid="invoice-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <User size={14} className="text-slate-400" />
            <select
              value={createdByFilter}
              onChange={(e) => setCreatedByFilter(e.target.value)}
              className="px-3 py-2 border rounded text-sm min-w-[160px]"
              data-testid="invoices-created-by-filter"
            >
              <option value="">All Users</option>
              {staffUsers?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[10px] border overflow-hidden">
        <table className="w-full" data-testid="invoices-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Date</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Invoice No.</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Client</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Reference</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Amount Due</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Created By</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Status</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center"><Loader2 className="animate-spin mx-auto text-primary" size={28} /></td></tr>
            ) : filtered?.length === 0 ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center">
                <Receipt size={48} className="mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">No invoices found</p>
              </td></tr>
            ) : filtered?.map((inv) => (
              <tr key={inv.id} className="hover:bg-orange-50/30 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-4 py-3 text-sm">{formatDate(inv.invoice_date || inv.created_at)}</td>
                <td className="px-4 py-3 text-sm font-medium">{inv.invoice_number}</td>
                <td className="px-4 py-3 text-sm">
                  {inv.client_id ? (
                    <Link to={`/clients/${inv.client_id}`} className="text-primary hover:underline" target="_blank">
                      {inv.client_name || inv.contact_name || '-'}
                    </Link>
                  ) : (inv.client_name || inv.contact_name || '-')}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{inv.reference || '-'}</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">${(inv.total || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-sm text-slate-600" data-testid={`invoice-created-by-${inv.id}`}>{inv.added_by_name || '-'}</td>
                <td className="px-4 py-3 text-center">
                  {statusBadge(inv.status)}
                  {inv.is_exported && exportedBadge()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setRemoveTarget(inv); }}
                      className="px-3 py-1.5 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      data-testid={`remove-invoice-${inv.id}`}
                    >
                      Remove
                    </button>
                    <Link
                      to={`/invoices/${inv.id}`}
                      className="px-3 py-1.5 text-xs bg-primary text-white rounded hover:bg-primary/90"
                      data-testid={`view-invoice-${inv.id}`}
                    >
                      Details
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RemoveModal
        isOpen={!!removeTarget}
        invoice={removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => deleteMutation.mutate(removeTarget.id)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

export default Invoices;
